import {prepareParams} from 'src/services/api/endpoints/_internal'
import {CompanyCredentials} from './CompanyEndpoint'
import {User} from './UserEndpoint'

import axios from './_axios'
import {AxiosResult, Paginate} from './_types'

const prefix = process.env.REACT_APP_AUTH_BASE_URL

export type AuthLogin = {
  email: string
  password: string
  keepLoggedIn: boolean
}

export type AuthLoginResponse = {
  accessToken: string
  refreshToken: string
}

export type AuthPassword = {
  newPassword: string
  currentPassword?: string
}

export type DefinePasswordDTO = {
  token: string
  newPassword: string
  keepLoggedIn?: boolean
}

export type AuthPasswordResponse = {
  statusCode: number
  error?: string
  message: string
}

export type SendConfirmationEmailBody = {
  email: string
  username: string
}

export type CreateApiKeysParams = {
  name: string
  userId: number
  expiresInDays?: number
}

export class CreateApiKeyResponseDTO {
  id: number
  plainKey: string
  prefix: string
  name: string
  expiresAt: string | null
  createdAt: string

  constructor(data: CreateApiKeyResponseDTO) {
    this.id = data.id
    this.plainKey = data.plainKey
    this.prefix = data.prefix
    this.name = data.name
    this.expiresAt = data.expiresAt
    this.createdAt = data.createdAt
  }
}

export type ListApiKeysParams = {
  isActive?: boolean
  userId: number
  page?: number
  limit?: number
}

export class ApiKeyMetadataDTO {
  id: number
  name: string
  maskedKey: string
  lastUsedAt: string | null
  expiresAt: string | null
  isActive: boolean
  isRevoked: boolean
  createdAt: string

  constructor(data: ApiKeyMetadataDTO) {
    this.id = data.id
    this.name = data.name
    this.maskedKey = data.maskedKey
    this.lastUsedAt = data.lastUsedAt
    this.expiresAt = data.expiresAt
    this.isActive = data.isActive
    this.isRevoked = data.isRevoked
    this.createdAt = data.createdAt
  }
}

export type UpdateApiKeyBody = {
  name?: string
  isActive?: boolean
}

export type OperationType = 'confirm-identity' | 'forgot-password'
export class AuthEndpoint {
  async login(data: AuthLogin): Promise<AxiosResult<AuthLoginResponse>> {
    return await axios.post(`${prefix}/login`, data)
  }

  async refresh(keepLoggedIn: boolean): Promise<AxiosResult<AuthLoginResponse>> {
    return await axios.post(`${prefix}/refresh`, {keepLoggedIn})
  }

  async me(): Promise<AxiosResult<User>> {
    return await axios.post(`${prefix}/me`)
  }

  async updatePassword(password: AuthPassword): Promise<AxiosResult<AuthPasswordResponse>> {
    return await axios.patch(`${prefix}/user/password`, password)
  }

  async updateCredentials(companyId: number): Promise<AxiosResult<CompanyCredentials>> {
    return await axios.patch(`${prefix}/companies/credentials`, {companyId})
  }

  async validateToken(token: string): Promise<AxiosResult<AuthLoginResponse>> {
    return await axios.post(`${prefix}/confirm-identity`, {token})
  }

  async definePassword(data: DefinePasswordDTO): Promise<AxiosResult<AuthLoginResponse>> {
    return await axios.post(`${prefix}/define-password`, data)
  }

  async forgotPassword(email: string): Promise<AxiosResult<boolean>> {
    return await axios.post(`${prefix}/forgot-password`, {email})
  }

  async resendMail(email: string, operation: OperationType): Promise<AxiosResult<boolean>> {
    return await axios.post(`${prefix}/resend-email`, {email, operation})
  }

  async sendConfirmationEmail(data: SendConfirmationEmailBody): Promise<AxiosResult<any>> {
    return await axios.post(`${prefix}/self-register/send-confirmation-email`, data)
  }

  async createApiKey(data: CreateApiKeysParams): Promise<AxiosResult<CreateApiKeyResponseDTO>> {
    return await axios.post(`${prefix}/api-keys`, data)
  }

  async listApiKeys(params: ListApiKeysParams): Promise<AxiosResult<Paginate<ApiKeyMetadataDTO>>> {
    return await axios.get(`${prefix}/api-keys`, {params: prepareParams(params)})
  }

  async revokeApiKey(id: number): Promise<AxiosResult<CreateApiKeyResponseDTO>> {
    return await axios.delete(`${prefix}/api-keys/${id}`)
  }

  async updateApiKey(id: number, data: UpdateApiKeyBody): Promise<AxiosResult<ApiKeyMetadataDTO>> {
    return await axios.patch(`${prefix}/api-keys/${id}`, data)
  }
}
